import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTabsContext } from "../../context/TabsContext";

const TabList = ({
  children,
  className = "",
  "aria-label": ariaLabel,
  ...rest
}) => {
  const { orientation, activeValue } = useTabsContext("TabList");
  const listRef = useRef(null);
  const [indicatorStyle, setIndicatorStyle] = useState({ opacity: 0 });

  const recalculate = useCallback(() => {
    const list = listRef.current;
    if (!list) return;
    const activeEl = list.querySelector('[data-state="active"]');
    if (!activeEl) {
      setIndicatorStyle((s) => ({ ...s, opacity: 0 }));
      return;
    }
    const listRect = list.getBoundingClientRect();
    const rect = activeEl.getBoundingClientRect();

    if (orientation === "vertical") {
      setIndicatorStyle({
        opacity: 1,
        transform: `translateY(${rect.top - listRect.top}px)`,
        height: rect.height,
        width: 1.5,
      });
    } else {
      setIndicatorStyle({
        opacity: 1,
        transform: `translateX(${rect.left - listRect.left}px)`,
        width: rect.width,
        height: 1.5,
      });
    }
  }, [orientation]);

  useLayoutEffect(() => {
    recalculate();
  },[recalculate, activeValue]);

  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(recalculate);
    ro.observe(list);
    window.addEventListener("resize", recalculate);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recalculate);
    };
  }, [recalculate]);

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation={orientation}
      className={`tabs-list ${
        orientation === "vertical" ? "tab-list-vertical" : "tab-list-horzitonal"
      } ${className}`}
      {...rest}
    >
      {children}
      <span
        aria-hidden="true"
        className="tabs-indicator"
        style={{
          ...indicatorStyle,
          left: orientation === "vertical" ? -1 : 0,
          bottom: orientation === "vertical" ? undefined : -1,
          top: orientation === "vertical" ? 0 : undefined,
        }}
      />
    </div>
  );
};

export default TabList;
